import React, { useEffect, useState, useRef } from 'react';
import { AntLocalization, AntAPI } from 'ante-react-kit';

function getInitials(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name) {
    const palette = ['#64748b', '#7c6f8a', '#8b6f47', '#5e7a6a', '#6b7488', '#846a7a', '#7a6d52'];
    let hash = 0;
    for (let i = 0; i < String(name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff;
    return palette[Math.abs(hash) % palette.length];
}

function fmtMoney(v) {
    if (v === null || v === undefined || isNaN(v)) return '0';
    try { return Number(v).toLocaleString('vi-VN'); } catch (e) { return String(v); }
}

export default function DetailPanel({ row, onConfirm }) {
    const [detail, setDetail] = useState(row || null);
    const [loading, setLoading] = useState(false);
    const apiRef = useRef(null);
    const reqSeqRef = useRef(0);
    if (!apiRef.current) apiRef.current = new AntAPI('entity', 'BusinessEntity');

    useEffect(() => {
        if (!row || !row.id) { setDetail(null); return; }
        setDetail(row); // hiển thị ngay với data list
        const seq = ++reqSeqRef.current;
        setLoading(true);
        apiRef.current.getDetail(row.id).then((res) => {
            if (seq !== reqSeqRef.current) return;
            const d = (res && (res.data || res)) || row;
            setDetail({ ...row, ...d });
        }).finally(() => {
            if (seq === reqSeqRef.current) setLoading(false);
        });
    }, [row && row.id]);

    if (!row) {
        return (
            <div className="apk-detail apk-detail--empty">
                <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                    <circle cx="28" cy="20" r="9" stroke="#cbd5e1" strokeWidth="2" />
                    <path d="M9 47c0-9 8-14 19-14s19 5 19 14" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <div className="apk-detail__empty-text">
                    {AntLocalization.translateText({ vi: 'Chọn một khách hàng để xem chi tiết', en: 'Select a customer to see details' })}
                </div>
            </div>
        );
    }

    const d = detail || row;
    const fullName = d.entity_full_name || d.entity_name || '—';
    const phone = d.entity_contact_phone;
    const email = d.entity_contact_email;
    const address = d.entity_address;
    const province = d.entity_adm1_name;
    const legalId = d.entity_legal_id;
    const loyalty = d.entity_loyalty_level || d.loyalty_level;
    const collections = Array.isArray(d.entity_collections) ? d.entity_collections : [];
    const tags = Array.isArray(d.tags) ? d.tags : [];
    const referalCode = d.entity_referal_code;
    const parentName = d.entity_parent_full_name;
    // Tài chính (per FE config BusinessEntity.json)
    const totalOrder = d.ar_credit_net_inc;   // Doanh thu = đã đặt hàng
    const remainDebt = d.balance_amt;          // Công nợ còn lại
    const walletAmt = d.wallet_amt;            // Số dư ví (TK trả trước)

    return (
        <div className="apk-detail">
            <div className="apk-detail__header">
                <div className="apk-detail__avatar" style={{ background: getAvatarColor(fullName) }}>
                    {getInitials(fullName)}
                </div>
                <div className="apk-detail__headline">
                    <div className="apk-detail__name">{fullName}</div>
                    <div className="apk-detail__sub">
                        {d.code ? <span className="apk-detail__code">#{d.code}</span> : null}
                        {loyalty ? <span className="apk-detail__chip apk-detail__chip--tier">{loyalty}</span> : null}
                    </div>
                </div>
            </div>

            <div className="apk-detail__body">
                {/* Liên hệ — chỉ render row có giá trị */}
                {(phone || email || address || province || legalId) && (
                    <div className="apk-detail__section">
                        {phone && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'SĐT', en: 'Phone' })}</span>
                                <span className="apk-detail__value">{phone}</span>
                            </div>
                        )}
                        {email && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'Email', en: 'Email' })}</span>
                                <span className="apk-detail__value">{email}</span>
                            </div>
                        )}
                        {(address || province) && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'Địa chỉ', en: 'Address' })}</span>
                                <span className="apk-detail__value">{address || province}</span>
                            </div>
                        )}
                        {legalId && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'CMND/MST', en: 'Legal ID' })}</span>
                                <span className="apk-detail__value">{legalId}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Phân loại — Hạng / Nhóm / Tags / Mã GT / Cấp trên */}
                {(loyalty || collections.length > 0 || tags.length > 0 || referalCode || parentName) && (
                    <div className="apk-detail__section">
                        {loyalty && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'Xếp hạng', en: 'Tier' })}</span>
                                <span className="apk-detail__value">
                                    <span className="apk-detail__chip apk-detail__chip--tier">{loyalty}</span>
                                </span>
                            </div>
                        )}
                        {collections.length > 0 && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'Nhóm', en: 'Groups' })}</span>
                                <span className="apk-detail__value apk-detail__value--wrap">
                                    {collections.map((c, i) => <span key={i} className="apk-detail__chip">{c}</span>)}
                                </span>
                            </div>
                        )}
                        {tags.length > 0 && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'Tags', en: 'Tags' })}</span>
                                <span className="apk-detail__value apk-detail__value--wrap">
                                    {tags.map((t, i) => <span key={i} className="apk-detail__chip">#{t}</span>)}
                                </span>
                            </div>
                        )}
                        {referalCode && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'Mã giới thiệu', en: 'Referral Code' })}</span>
                                <span className="apk-detail__value">{referalCode}</span>
                            </div>
                        )}
                        {parentName && (
                            <div className="apk-detail__row">
                                <span className="apk-detail__label">{AntLocalization.translateText({ vi: 'Cấp trên', en: 'Referred By' })}</span>
                                <span className="apk-detail__value">{parentName}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Tài chính — Đặt hàng / Còn nợ / Số dư ví */}
                <div className="apk-detail__section">
                    <div className="apk-detail__stats">
                        <div className="apk-detail__stat">
                            <div className="apk-detail__stat-label">{AntLocalization.translateText({ vi: 'Đặt hàng', en: 'Revenue' })}</div>
                            <div className="apk-detail__stat-value">
                                {fmtMoney(totalOrder)}
                            </div>
                        </div>
                        <div className="apk-detail__stat">
                            <div className="apk-detail__stat-label">{AntLocalization.translateText({ vi: 'Còn nợ', en: 'Outstanding' })}</div>
                            <div className={'apk-detail__stat-value' + (remainDebt > 0 ? ' apk-detail__stat-value--warn' : (remainDebt < 0 ? ' apk-detail__stat-value--neg' : ''))}>
                                {fmtMoney(remainDebt)}
                            </div>
                        </div>
                        <div className="apk-detail__stat">
                            <div className="apk-detail__stat-label">{AntLocalization.translateText({ vi: 'Số dư ví', en: 'Wallet' })}</div>
                            <div className="apk-detail__stat-value">
                                {fmtMoney(walletAmt)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="apk-detail__footer">
                <button
                    type="button"
                    className="apk-confirm-btn"
                    onClick={() => onConfirm && onConfirm(d)}
                >
                    <span className="apk-confirm-btn__label">
                        {AntLocalization.translateText({ vi: 'Chọn khách hàng này', en: 'Select this customer' })}
                    </span>
                    <svg className="apk-confirm-btn__arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
    );
}
